import React, { createContext, useState, useEffect, useContext } from "react";
import { getToken, removeToken, setToken } from "../utils/tokenManager";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const AUTH_TOKEN_MAP = {
    user: "user_auth_token",
    business: "business_auth_token",
    admin: "admin_auth_token",
    volunteer: "volunteer_auth_token",
    accounts: "accounts_auth_token",
  };

  const getAuthType = () => {
    for (const [type, token] of Object.entries(AUTH_TOKEN_MAP)) {
      if (getToken(token)) return type;
    }
    return "other";
  };

  const [authState, setAuthState] = useState(() => ({
    isAuthenticated: Object.values(AUTH_TOKEN_MAP).some(
      (token) => !!getToken(token),
    ),
    authType: getAuthType(),
    role: localStorage.getItem("user_role") || null,
    sidebarTabs: JSON.parse(localStorage.getItem("sidebar_tabs") || "[]"),
    store_id: localStorage.getItem("store_id") || null,
    business_id: localStorage.getItem("business_id") || null,
  }));

  useEffect(() => {
    console.log(`Authentication Type: ${authState.authType}`);
  }, [authState.authType]);
  const api = process.env.REACT_APP_API_URL;

  const getAuthTokenKey = (redirectTo) => {
    if (redirectTo?.startsWith("/business")) return "business_auth_token";
    if (redirectTo?.startsWith("/admin")) return "admin_auth_token";
    if (redirectTo?.startsWith("/volunteer")) return "volunteer_auth_token";
    if (redirectTo?.startsWith("/accounts")) return "accounts_auth_token";
    return "user_auth_token";
  };

  const applySuccessfulAuth = (data, rememberMe = false) => {
    const auth_token = data.token;
    const token = getAuthTokenKey(data.redirectTo);

    removeToken("user_auth_token");
    removeToken("business_auth_token");
    removeToken("admin_auth_token");
    removeToken("volunteer_auth_token");
    removeToken("accounts_auth_token");

    setToken({ rememberMe, auth_token, token });

    if (data.role) {
      localStorage.setItem("user_role", data.role);
    }
    if (data.sidebarTabs) {
      localStorage.setItem("sidebar_tabs", JSON.stringify(data.sidebarTabs));
    } else {

      localStorage.removeItem("sidebar_tabs");
    }

    let businessIdToSave = null;
    let storeIdToSave = null;

    if (data.businessId) {
      businessIdToSave = data.businessId;
      localStorage.setItem("business_id", businessIdToSave);
    } else {
      localStorage.removeItem("business_id");
    }

    if (data.subaccount && data.subaccount.store_id) {
      storeIdToSave = data.subaccount.store_id;
      localStorage.setItem("store_id", storeIdToSave);
    } else {
      localStorage.removeItem("store_id");
    }

    setAuthState({
      isAuthenticated: true,
      authType: getAuthType(),
      role: data.role || null,
      sidebarTabs: data.sidebarTabs || [],
      store_id: storeIdToSave,
      business_id: businessIdToSave,
    });
  };

  const login = async ({ email, password, otp, rememberMe }) => {
    const loginApi = api + "/auth/login";
    try {
      const response = await fetch(loginApi, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password, otp }),
      });
      const data = await response.json();
      console.log(data);
      if (data.success) {
        applySuccessfulAuth(data, rememberMe);
      }
      return {
        message: data.message,
        redirectTo: data.redirectTo,
        success: data.success,
      };
    } catch (error) {
      console.log(error);
      return {
        message: "Error in login",
        redirectTo: null,
        success: false,
      };
    }
  };

  const logout = async (type) => {
    let token;
    if (type === "user") token = "user_auth_token";
    else if (type === "business") token = "business_auth_token";
    else if (type === "admin") token = "admin_auth_token";
    else if (type === "volunteer") token = "volunteer_auth_token";
    else if (type === "accounts") token = "accounts_auth_token";
    removeToken(token);
    setAuthState({
      isAuthenticated: false,
      authType: "other", // Default to 'other' after logout
      role: null,
      sidebarTabs: [],
      store_id: null,
      business_id: null,
    });
    localStorage.removeItem("user_role");
    localStorage.removeItem("sidebar_tabs");
    localStorage.removeItem("store_id");
    localStorage.removeItem("business_id");
  };

  const register = async (payload) => {
    const registerApi = api + "/auth/register";
    try {
      const response = await fetch(registerApi, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          first_name: payload.first_name,
          last_name: payload.last_name,
          email: payload.email,
          phone_number: payload.phone_number,
          password: payload.password,
        }),
      });
      const data = await response.json();

      if (response.ok) {
        console.log("Registration successful:", data);
        // navigate('/registration2');
      } else {
        alert(`Registration failed: ${data.message}`);
      }
    } catch (error) {
      console.error("Error during registration:", error);
      alert("An error occurred during registration. Please try again.");
    }
  };

  const sendOtp = async (payload) => {
    // const token = getToken();

    const sendOtpAPi = api + "/auth/send-otp";
    let data;
    try {
      const response = await fetch(sendOtpAPi, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: payload.email,
        }),
      });
      data = await response.json();
      if (response.ok) {
        console.log("OTP send successfully");
      } else {
        console.log(`Failed: ${data.message}`);
      }
    } catch (error) {
      console.error("Error during Login with otp:", error);
      alert("An error occurred during Login. Please try again.");
    } finally {
      return data;
    }
  };

  const googleLogin = async ({ credential, rememberMe }) => {
    const googleLoginApi = api + "/auth/google";

    try {
      const response = await fetch(googleLoginApi, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: credential }),
      });
      const data = await response.json();

      if (response.ok && data.success) {
        applySuccessfulAuth(data, rememberMe);
      }

      return {
        message: data.message,
        redirectTo: data.redirectTo,
        success: data.success,
      };
    } catch (error) {
      console.log(error);
      return {
        message: "Error in Google login",
        redirectTo: null,
        success: false,
      };
    }
  };

  const verifyRegistrationOtp = async (email, otp, rememberMe = false) => {
    const apiEndpoint = api + "/auth/verify-otp";
    try {
      const response = await fetch(apiEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, otp }),
      });
      const data = await response.json();
      console.log(data);
      if (response.ok && data.success) {
        applySuccessfulAuth(data, rememberMe);
      }
      return {
        message: data.message,
        redirectTo: data.redirectTo,
        success: data.success,
      };
    } catch (error) {
      console.log(error);
      return {
        message: "Error verifying OTP",
        redirectTo: null,
        success: false,
      };
    }
  };

  const changePassword = async ({ currentPassword, newPassword }) => {
    try {
      const type = authState.authType;
      
      let endpoint = api + "/users/change-password"; // Default
      if (type === 'business') {
        endpoint = api + "/business/change-password";
      } else if (type === 'accounts') {
        endpoint = api + "/accounts/change-password";
      }
      
      const token = getToken(`${type}_auth_token`);
      
      const response = await fetch(endpoint, {
        method: type === 'business' ? "POST" : "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          currentPassword,
          newPassword
        })
      });
      const data = await response.json();
      return { 
        success: response.ok && data.success !== false, 
        message: data.message || "Password changed successfully" 
      };
    } catch (error) {
      console.log(error);
      return { success: false, message: "Failed to change password" };
    }
  };

  const resetPassword = async ({ email, otp, password }) => {
    const apiEndpoint = api + "/auth/forget-password";
    try {
      const response = await fetch(apiEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, otp, password }),
      });
      const data = await response.json();
      return {
        message: data.message || "Password reset successfully",
        success: data.success ?? response.ok,
      };
    } catch (error) {
      console.log(error);
      return {
        message: "Error resetting password",
        success: false,
      };
    }
  };

  return (
    <AuthContext.Provider
      value={{
        authState,
        login,
        logout,
        register,
        sendOtp,
        googleLogin,
        verifyRegistrationOtp,
        resetPassword,
        changePassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the AuthContext
export const useAuth = () => {
  return useContext(AuthContext);
};
